import React, {useEffect, useRef, useState} from 'react';

const SET_IS_SCROLLING_TIMEOUT = 150;
/**
 * Tracks scroll position and scrolling state of a container element.
 *
 * Masonic's built-in `useScroller` only tracks `window` scroll via
 * `@react-hook/window-scroll`. This hook is needed because our masonry grid
 * scrolls inside an arbitrary DOM element (e.g. a layout panel), not the
 * window itself.
 */
function useContainerScroller(scrollRef: React.RefObject<HTMLElement>) {
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const isScrollingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    const handleScroll = () => {
      setScrollTop(el.scrollTop);
      setIsScrolling(true);

      if (isScrollingTimeoutRef.current) {
        clearTimeout(isScrollingTimeoutRef.current);
      }
      isScrollingTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, SET_IS_SCROLLING_TIMEOUT);
    };

    el.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (isScrollingTimeoutRef.current) {
        clearTimeout(isScrollingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {scrollTop, isScrolling};
}

export default useContainerScroller;
